import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Save, X, Youtube, Play } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Extract YouTube video ID from various URL formats
const getYouTubeVideoId = (url) => {
  if (!url) return null;
  
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/shorts\/([^&\n?#]+)/
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

const VideoAdmin = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    youtube_url: '',
    title: '',
    description: ''
  });

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const response = await fetch(`${API_URL}/api/videos?active_only=false`);
      if (response.ok) {
        const data = await response.json();
        setVideos(data);
      }
    } catch (error) {
      console.error('Error fetching videos:', error);
      toast.error('Eroare la încărcarea videoclipurilor');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.youtube_url || !formData.title) {
      toast.error('Completează link-ul YouTube și titlul');
      return;
    }

    // Validate YouTube URL
    const videoId = getYouTubeVideoId(formData.youtube_url);
    if (!videoId) {
      toast.error('Link YouTube invalid. Folosește un link valid de YouTube.');
      return;
    }

    try {
      const url = editingId 
        ? `${API_URL}/api/videos/${editingId}`
        : `${API_URL}/api/videos`;
      
      const method = editingId ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingId ? 'Video actualizat!' : 'Video adăugat!');
        resetForm();
        fetchVideos();
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast.error('Eroare la salvare');
    }
  };

  const handleEdit = (video) => {
    setFormData({
      youtube_url: video.youtube_url,
      title: video.title,
      description: video.description || ''
    });
    setEditingId(video.id);
    setShowAddForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Ești sigur că vrei să ștergi acest video?')) return;

    try {
      const response = await fetch(`${API_URL}/api/videos/${id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('Video șters');
        fetchVideos();
      }
    } catch (error) {
      toast.error('Eroare la ștergere');
    }
  };

  const resetForm = () => {
    setFormData({
      youtube_url: '',
      title: '',
      description: ''
    });
    setEditingId(null);
    setShowAddForm(false);
  };

  const previewVideoId = getYouTubeVideoId(formData.youtube_url);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2196f3]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[#0d2e5d]">Videoclipuri YouTube</h2>
          <p className="text-gray-600 text-sm">Adaugă videoclipuri cu procesul de curățare</p>
        </div>
        <Button onClick={() => setShowAddForm(true)} className="bg-[#5cd26f] hover:bg-[#4bc160]">
          <Plus className="w-4 h-4 mr-2" />
          Adaugă Video
        </Button>
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <Card className="border-2 border-[#2196f3]">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>{editingId ? 'Editează Video' : 'Adaugă Video Nou'}</span>
              <Button variant="ghost" size="icon" onClick={resetForm}>
                <X className="w-5 h-5" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Link YouTube *
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Youtube className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500" />
                    <Input
                      value={formData.youtube_url}
                      onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
                      placeholder="https://www.youtube.com/watch?v=..."
                      className="pl-10"
                      required
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Acceptă link-uri: youtube.com/watch, youtu.be, youtube.com/shorts
                </p>
              </div>

              {/* Video Preview */}
              {previewVideoId && (
                <div className="rounded-lg overflow-hidden border">
                  <div className="aspect-video">
                    <iframe
                      src={`https://www.youtube.com/embed/${previewVideoId}`}
                      title="Preview"
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Titlu *
                </label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="ex: Cum curățăm o canapea cu pete"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descriere (opțional)
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descriere scurtă a videoclipului..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2196f3]"
                />
              </div>

              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={resetForm}>
                  Anulează
                </Button>
                <Button type="submit" className="bg-[#2196f3] hover:bg-[#1976d2]">
                  <Save className="w-4 h-4 mr-2" />
                  {editingId ? 'Salvează' : 'Adaugă'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Videos List */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-700">
          Videoclipuri ({videos.length})
        </h3>
        
        {videos.length === 0 ? (
          <Card className="p-8 text-center">
            <Youtube className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500">Nu există videoclipuri încă.</p>
            <p className="text-gray-400 text-sm mt-2">
              Adaugă primul tău videoclip YouTube.
            </p>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {videos.map((video) => {
              const videoId = getYouTubeVideoId(video.youtube_url);
              return (
                <Card key={video.id} className={`overflow-hidden ${!video.is_active ? 'opacity-50' : ''}`}>
                  <div className="aspect-video relative">
                    {videoId ? (
                      <img
                        src={`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`}
                        alt={video.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                        <Youtube className="w-12 h-12 text-gray-400" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                      <a
                        href={video.youtube_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center"
                      >
                        <Play className="w-8 h-8 text-white ml-1" />
                      </a>
                    </div>
                  </div>
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-700 truncate">{video.title}</p>
                        {video.description && (
                          <p className="text-sm text-gray-500 truncate">{video.description}</p>
                        )}
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(video)}
                          className="h-8 w-8 hover:bg-blue-50 hover:text-[#2196f3]"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(video.id)}
                          className="h-8 w-8 hover:bg-red-50 hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Instructions */}
      <Card className="bg-red-50 border-red-200">
        <CardContent className="p-4">
          <h3 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
            <Youtube className="w-5 h-5" />
            Cum să adaugi un video YouTube:
          </h3>
          <ol className="list-decimal list-inside text-gray-700 space-y-1 text-sm">
            <li>Deschide videoclipul pe YouTube</li>
            <li>Copiază link-ul din bara de adrese</li>
            <li>Lipește link-ul în câmpul de mai sus</li>
            <li>Adaugă un titlu și salvează</li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
};

export default VideoAdmin;
